import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import TagMultiSelect from '../../components/crm/TagMultiSelect';
import InfoTooltip from '../../components/ui/InfoTooltip';
import {
  fetchProspectFilterMeta,
  fetchEntrepriseSearch,
  fetchEntrepriseImportJob,
  startEntrepriseImportJob,
  importProspects,
  searchOdintProspect,
  createProspect,
} from '../../api/prospects';
import {
  ENTREPRISE_GOUV_IMPORT_MAPPING,
  entreprisePreviewToImportRow,
} from '../../utils/entrepriseGouvImport';

const SEARCH_TOOLTIP =
  'Annuaire open data (État). Une ligne correspond à un établissement. Vingt-cinq lignes par page, plafond d’environ 10 000 résultats. Au-delà, utilisez l’import CSV.';

const POSTAL_CODE_TOOLTIP =
  'Format obligatoire : cinq chiffres. Exemples : 75001, 69002.';

const APE_TOOLTIP =
  'Format XX.XXZ : le point est obligatoire. Exemples : 62.01Z, 69.10Z. Champ optionnel. Laisser vide pour tous les secteurs du code postal.';

const KEYWORD_TOOLTIP =
  'Champ optionnel, minimum trois caractères. Filtre la raison sociale ou l’activité. Exemple : boulangerie. Laisser vide pour toutes les entreprises du code postal.';

const TABLE_NAME_SEARCH_TOOLTIP =
  'Recherche un nom dans le même code postal et la même activité (XX.XXZ), sur l’ensemble des pages. Minimum trois caractères ; recherche partielle acceptée. Exemple : « mart » pour « Martin SAS ».';

const TABLE_NAME_MIN_LEN = 3;

const ODINT_COMPANY_TOOLTIP =
  'Raison sociale ou nom d’usage. ODINT complète téléphone, e-mail et site web. Ignoré si un SIREN ou un SIRET est renseigné.';

const ODINT_SIREN_SIRET_TOOLTIP =
  'SIREN : neuf chiffres. SIRET : quatorze chiffres. Exemples : 552032534, 55203253400012. Espaces autorisés. Prioritaire sur le nom saisi.';

const SIRENE_EXPORT_URL = 'https://annuaire-entreprises.data.gouv.fr/export-sirene';

function buildProspectPayloadFromOdint(companyQuery, candidate, tags) {
  const company = String(candidate?.name || companyQuery).trim();
  const phone = String(candidate?.phone || '').trim();
  const mobile = String(candidate?.mobilePhone || '').trim();
  return {
    company,
    fullName: company,
    phone: phone || mobile,
    mobilePhone: mobile && mobile !== phone ? mobile : mobile && !phone ? mobile : '',
    email: String(candidate?.email || '').trim(),
    website: String(candidate?.website || '').trim(),
    address: String(candidate?.address || '').trim(),
    postalCode: String(candidate?.postalCode || '').trim(),
    city: String(candidate?.city || '').trim(),
    siret: String(candidate?.siret || '').trim(),
    ape: String(candidate?.ape || '').trim(),
    tags: tags || [],
    status: 'prospect',
    priority: 'low',
  };
}

/** apple-touch-icon du site annuaire-entreprises.data.gouv.fr (public/favicons). */
const ANNUAIRE_ENTREPRISES_ICON_SRC = '/assets/annuaire-entreprises-icon.png';

function AnnuaireEntreprisesLinkIcon({ className = '' }) {
  return (
    <img
      src={ANNUAIRE_ENTREPRISES_ICON_SRC}
      alt=""
      width={32}
      height={32}
      className={`h-8 w-8 shrink-0 rounded-[4px] object-contain ${className}`.trim()}
      aria-hidden
    />
  );
}

const IMPORT_BATCH_SIZE = 400;
const JOB_POLL_MS = 1500;

function jobToOverlay(job, { background = false } = {}) {
  if (!job || job.status !== 'running') return null;
  const prefix = background ? 'Import en arrière-plan' : 'Import en cours';
  const saved = (job.created ?? 0) + (job.updated ?? 0);
  return {
    title: prefix,
    detail: `Page ${job.page} / ${job.totalPages} — ${saved} fiche(s) déjà enregistrée(s), ${job.collected} établissement(s) traité(s)…`,
    percent: job.percent,
    background,
  };
}

function ImportProgressOverlay({ progress, onClose }) {
  if (!progress) return null;
  const pct =
    progress.percent != null
      ? Math.min(100, Math.max(0, Math.round(progress.percent)))
      : null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-elevation-3 text-center">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-base font-medium text-foreground">{progress.title}</p>
        <p className="mt-2 text-sm text-muted-foreground">{progress.detail}</p>
        {progress.background ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Vous pouvez quitter cette page : l&apos;import continue sur le serveur. Revenez ici ou
            cliquez sur « Lancer la recherche » pour suivre la progression.
          </p>
        ) : null}
        {pct != null ? (
          <div className="mt-5">
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-2 text-xs tabular-nums text-muted-foreground">{pct} %</p>
          </div>
        ) : null}
        {progress.background && onClose ? (
          <Button type="button" variant="outline" size="sm" className="mt-5" onClick={onClose}>
            Masquer (import continue)
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function ProspectEntrepriseSearchPage() {
  const navigate = useNavigate();
  const [codePostal, setCodePostal] = useState('');
  const [ape, setApe] = useState('');
  const [q, setQ] = useState('');
  const [tableNameQ, setTableNameQ] = useState('');
  const [tableNameError, setTableNameError] = useState(null);
  const [page, setPage] = useState(1);

  const [tagsForAll, setTagsForAll] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchResult, setSearchResult] = useState(null);

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [backgroundJob, setBackgroundJob] = useState({ status: 'idle' });
  const [overlayDismissed, setOverlayDismissed] = useState(false);

  const [odintQuery, setOdintQuery] = useState('');
  const [odintSirenSiret, setOdintSirenSiret] = useState('');
  const [odintLoading, setOdintLoading] = useState(false);
  const [odintError, setOdintError] = useState(null);
  const [odintEmpty, setOdintEmpty] = useState(false);
  const [odintFields, setOdintFields] = useState([]);
  const [odintCandidate, setOdintCandidate] = useState(null);
  const [odintChoices, setOdintChoices] = useState([]);
  const [odintSelectedId, setOdintSelectedId] = useState(null);
  const [odintCreating, setOdintCreating] = useState(false);

  const backgroundImportRunning = backgroundJob?.status === 'running';

  const applyJobState = (job) => {
    setBackgroundJob(job);
    if (job?.status === 'running' && job.params) {
      if (job.params.codePostal) setCodePostal(job.params.codePostal);
      if (job.params.activitePrincipale != null) setApe(job.params.activitePrincipale);
      if (job.params.q != null) setQ(job.params.q);
    }
    if (job?.status === 'running') {
      if (!overlayDismissed) {
        setImportProgress(jobToOverlay(job, { background: true }));
      }
    } else {
      setImportProgress(null);
      setOverlayDismissed(false);
    }
    if (job?.status === 'completed') {
      setImportResult({
        created: job.created ?? 0,
        updated: job.updated ?? 0,
        skipped: job.skipped ?? 0,
      });
      setImportError(job.error || null);
    }
    if (job?.status === 'failed') {
      setImportError(job.error || 'Import échoué.');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const job = await fetchEntrepriseImportJob();
        if (!cancelled) applyJobState(job);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!backgroundImportRunning) return undefined;
    const id = setInterval(async () => {
      try {
        const job = await fetchEntrepriseImportJob();
        applyJobState(job);
      } catch (e) {
        console.error(e);
      }
    }, JOB_POLL_MS);
    return () => clearInterval(id);
  }, [backgroundImportRunning, overlayDismissed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCampaigns(true);
      try {
        const res = await fetchProspectFilterMeta();
        if (!cancelled) setCampaignOptions(res?.data?.campaigns ?? []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setCampaignOptions([]);
      } finally {
        if (!cancelled) setLoadingCampaigns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCampaign = useMemo(
    () => campaignOptions.find((c) => c.id === selectedCampaignId) || null,
    [campaignOptions, selectedCampaignId],
  );

  const importTags = useMemo(() => {
    const fromCampaign = Array.isArray(selectedCampaign?.tags) ? selectedCampaign.tags : [];
    return Array.from(
      new Set([...fromCampaign, ...tagsForAll].map((t) => String(t).trim()).filter(Boolean)),
    );
  }, [selectedCampaign, tagsForAll]);

  const results = searchResult?.results ?? [];
  const totalLegalUnits = searchResult?.totalLegalUnits ?? 0;
  const totalCapped = searchResult?.totalCapped ?? false;
  const perPage = searchResult?.perPage ?? 25;
  const pageNum = searchResult?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(totalLegalUnits / perPage));

  const resultPostal = searchResult?.codePostal || codePostal.trim();
  const activeTableKeyword = useMemo(() => {
    const apiQ = String(searchResult?.q ?? '').trim();
    const cp = String(resultPostal || '').replace(/\D/g, '').slice(0, 5);
    if (!apiQ || (cp && apiQ === cp)) return '';
    return apiQ;
  }, [searchResult?.q, resultPostal]);

  const rowKey = (r) => r.siret || r.siren;

  const toggleRow = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllOnPage = () => {
    setSelectedKeys(new Set(results.map((r) => rowKey(r)).filter(Boolean)));
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const runTableNameSearch = () => {
    const next = tableNameQ.trim();
    setTableNameError(null);
    if (next.length > 0 && next.length < TABLE_NAME_MIN_LEN) {
      setTableNameError(
        `Minimum ${TABLE_NAME_MIN_LEN} caractères pour une recherche par nom (terme partiel).`,
      );
      return;
    }
    setQ(next);
    void runSearch(1, {
      keyword: next,
      nameFilter: next.length >= TABLE_NAME_MIN_LEN,
      fromTable: true,
    });
  };

  const clearTableNameSearch = () => {
    setTableNameQ('');
    setTableNameError(null);
    setQ('');
    void runSearch(1, { keyword: '', nameFilter: false });
  };

  const runSearch = async (pageNum = 1, overrides = {}) => {
    setSearching(true);
    setSearchError(null);
    setTableNameError(null);
    try {
      const job = await fetchEntrepriseImportJob();
      applyJobState(job);
      if (job?.status === 'running') {
        setOverlayDismissed(false);
        setImportProgress(jobToOverlay(job, { background: true }));
        return;
      }

      setImportResult(null);
      setImportError(null);
      const keyword =
        overrides.keyword !== undefined ? overrides.keyword : q;
      const keywordTrim = String(keyword ?? '').trim();
      if (keywordTrim.length > 0 && keywordTrim.length < TABLE_NAME_MIN_LEN) {
        const shortMsg = `Minimum ${TABLE_NAME_MIN_LEN} caractères pour une recherche par nom (terme partiel).`;
        if (overrides.fromTable) setTableNameError(shortMsg);
        else setSearchError(shortMsg);
        return;
      }
      if (overrides.keyword !== undefined) {
        setQ(keywordTrim);
      }
      const nameFilter =
        overrides.nameFilter !== undefined
          ? overrides.nameFilter
          : keywordTrim.length >= TABLE_NAME_MIN_LEN;
      const data = await fetchEntrepriseSearch({
        codePostal,
        activitePrincipale: ape,
        q: keywordTrim,
        page: pageNum,
        perPage: 25,
        nameFilter,
      });
      setSearchResult(data);
      setPage(pageNum);
      setSelectedKeys(new Set());
      const cp = String(codePostal || '').replace(/\D/g, '').slice(0, 5);
      const apiQ = String(data?.q ?? '').trim();
      if (nameFilter && apiQ && apiQ !== cp) setTableNameQ(apiQ);
      else if (overrides.keyword !== undefined) setTableNameQ(keywordTrim);
    } catch (e) {
      setSearchError(e?.message || 'Recherche impossible.');
    } finally {
      setSearching(false);
    }
  };

  const clearOdintResult = () => {
    setOdintEmpty(false);
    setOdintFields([]);
    setOdintCandidate(null);
    setOdintChoices([]);
    setOdintSelectedId(null);
    setOdintError(null);
  };

  const selectOdintChoice = (option) => {
    if (!option?.candidate) return;
    setOdintSelectedId(option.candidate.id || null);
    setOdintCandidate(option.candidate);
    setOdintFields(Array.isArray(option.fields) ? option.fields : []);
  };

  const runOdintSearch = async () => {
    const company = odintQuery.trim();
    const sirenSiret = odintSirenSiret.trim();
    if (!company && !sirenSiret) return;
    setOdintLoading(true);
    setOdintError(null);
    setOdintEmpty(false);
    setOdintFields([]);
    setOdintCandidate(null);
    setOdintChoices([]);
    setOdintSelectedId(null);
    try {
      const res = await searchOdintProspect({
        company,
        sirenSiret,
        postalCode: codePostal.trim(),
      });
      const choiceList = Array.isArray(res?.data?.candidates)
        ? res.data.candidates
        : [];
      if (res?.data?.requiresChoice && choiceList.length >= 2) {
        setOdintChoices(choiceList);
        return;
      }
      const fields = Array.isArray(res?.data?.fields) ? res.data.fields : [];
      const candidate = res?.data?.candidate || null;
      if (!candidate) {
        setOdintEmpty(true);
        return;
      }
      setOdintFields(fields);
      setOdintCandidate(candidate);
    } catch (e) {
      setOdintError(e?.message || 'Recherche ODINT impossible.');
    } finally {
      setOdintLoading(false);
    }
  };

  const createProspectFromOdint = async () => {
    if (!odintCandidate) return;
    setOdintCreating(true);
    setOdintError(null);
    try {
      const payload = buildProspectPayloadFromOdint(odintQuery, odintCandidate, importTags);
      const res = await createProspect(payload);
      const p = res?.data?.prospect;
      clearOdintResult();
      setOdintQuery('');
      setOdintSirenSiret('');
      navigate('/prospect-contact-directory');
    } catch (e) {
      setOdintError(e?.message || 'Création de la fiche impossible.');
    } finally {
      setOdintCreating(false);
    }
  };

  const runImportRows = async (
    previews,
    progressLabel,
    percentFrom = 0,
    percentTo = 100,
  ) => {
    const rows = previews.map(entreprisePreviewToImportRow);
    let created = 0;
    let updated = 0;
    let skipped = 0;
    const batchCount = Math.ceil(rows.length / IMPORT_BATCH_SIZE) || 1;
    const span = percentTo - percentFrom;

    for (let i = 0; i < rows.length; i += IMPORT_BATCH_SIZE) {
      const batchIndex = Math.floor(i / IMPORT_BATCH_SIZE) + 1;
      const chunkLen = Math.min(IMPORT_BATCH_SIZE, rows.length - i);
      const ratio = rows.length ? (i + chunkLen) / rows.length : 1;
      setImportProgress({
        title: progressLabel,
        detail: `Enregistrement ${batchIndex} / ${batchCount} (${rows.length} fiche(s))…`,
        percent: percentFrom + ratio * span,
      });
      const chunk = rows.slice(i, i + IMPORT_BATCH_SIZE);
      const res = await importProspects(
        chunk,
        ENTREPRISE_GOUV_IMPORT_MAPPING,
        importTags,
        ['fullName'],
      );
      const data = res?.data ?? {};
      created += data.created ?? 0;
      updated += data.updated ?? 0;
      skipped +=
        typeof data.skipped === 'number'
          ? data.skipped
          : (data.errors ?? []).length;
    }

    return { created, updated, skipped };
  };

  const handleImport = async () => {
    const toImport = results.filter((r) => selectedKeys.has(rowKey(r)));
    if (!toImport.length || importing || backgroundImportRunning) return;
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    setImportProgress({
      title: 'Import en cours',
      detail: `${toImport.length} prospect(s) sélectionné(s)…`,
      percent: 0,
    });
    try {
      const totals = await runImportRows(toImport, 'Import en cours');
      setImportResult(totals);
    } catch (e) {
      setImportError(e?.message || "Erreur lors de l'import.");
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const handleImportAll = async () => {
    if (!searchResult || importing || backgroundImportRunning) return;
    setImportError(null);
    setImportResult(null);
    setOverlayDismissed(false);

    try {
      const job = await startEntrepriseImportJob({
        codePostal,
        activitePrincipale: ape,
        q,
        tagsForAll: importTags,
      });
      applyJobState(job);
    } catch (e) {
      try {
        const job = await fetchEntrepriseImportJob();
        if (job?.status === 'running') {
          applyJobState(job);
          return;
        }
      } catch {
        /* ignore */
      }
      setImportError(e?.message || "Impossible de démarrer l'import.");
    }
  };

  const importedTotal =
    importResult != null ? (importResult.created ?? 0) + (importResult.updated ?? 0) : 0;

  return (
    <div className="min-h-screen bg-background">
      <ImportProgressOverlay
        progress={importProgress}
        onClose={
          importProgress?.background
            ? () => {
                setOverlayDismissed(true);
                setImportProgress(null);
              }
            : undefined
        }
      />
      <Helmet>
        <title>Rechercher des prospects - CRM</title>
      </Helmet>
      <Header />
      <Breadcrumb />
      <main className="main-content max-w-5xl mx-auto pb-12">
        <Button
          variant="ghost"
          size="sm"
          iconName="ArrowLeft"
          iconPosition="left"
          className="mb-4"
          onClick={() => navigate('/prospect-contact-directory')}
        >
          Annuaire
        </Button>

        <div className="mb-6 flex flex-wrap items-start gap-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-1">
              Rechercher des prospects
            </h1>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Code postal obligatoire, APE et mot-clé optionnels. Importez les lignes choisies en
              fiches prospect.
            </p>
            <a
              href={SIRENE_EXPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex max-w-2xl items-center gap-2.5 rounded-md border border-[#ddd] bg-[#f6f6f6] px-3 py-2 text-sm text-[#000091] shadow-sm transition-colors hover:bg-[#ececec] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#000091]/40"
            >
              <AnnuaireEntreprisesLinkIcon />
              <span className="min-w-0 flex-1">
              <span className="mt-0.5 block truncate text-xs font-normal text-muted-foreground">
                  {SIRENE_EXPORT_URL}
                </span>
                <span className="font-medium text-[#000091]">
                  Accès à l'annuaire des entreprises
                </span>
                
              </span>
            </a>
          </div>
          <InfoTooltip text={SEARCH_TOOLTIP} className="mt-1" />
        </div>

        <section className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4 mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="Code postal"
              labelTooltip={POSTAL_CODE_TOOLTIP}
              value={codePostal}
              onChange={(e) => setCodePostal(e.target.value)}
              autoComplete="postal-code"
            />
            <Input
              label="Code APE / NAF (optionnel)"
              labelTooltip={APE_TOOLTIP}
              value={ape}
              onChange={(e) => setApe(e.target.value)}
              autoComplete="off"
            />
            <Input
              label="Mot-clé (optionnel)"
              labelTooltip={KEYWORD_TOOLTIP}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              iconName="Search"
              disabled={searching || (!codePostal.trim() && !backgroundImportRunning)}
              onClick={() => runSearch(1)}
            >
              {searching
                ? 'Recherche…'
                : backgroundImportRunning
                  ? 'Voir la progression import'
                  : 'Lancer la recherche'}
            </Button>
          </div>
          {backgroundImportRunning ? (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Import en cours sur le serveur ({Math.round(backgroundJob.percent || 0)} %). Un seul
              import à la fois — « Tout importer » est désactivé jusqu&apos;à la fin.
            </p>
          ) : null}
          {searchError ? <p className="text-sm text-destructive">{searchError}</p> : null}

          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Rechercher une entreprise"
                  labelTooltip={ODINT_COMPANY_TOOLTIP}
                  value={odintQuery}
                  onChange={(e) => setOdintQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runOdintSearch();
                    }
                  }}
                  autoComplete="organization"
                />
                <Input
                  label="SIREN / SIRET (optionnel)"
                  labelTooltip={ODINT_SIREN_SIRET_TOOLTIP}
                  value={odintSirenSiret}
                  onChange={(e) => setOdintSirenSiret(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runOdintSearch();
                    }
                  }}
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>
              <Button
                type="button"
                iconName="Search"
                disabled={
                  odintLoading || (!odintQuery.trim() && !odintSirenSiret.trim())
                }
                loading={odintLoading}
                onClick={runOdintSearch}
                className="lg:shrink-0"
              >
                {odintLoading ? 'ODINT…' : 'Recherche ODINT'}
              </Button>
            </div>

            {odintLoading ? (
              <p className="text-sm text-muted-foreground">
                Recherche d&apos;informations ODINT (Opendata Intelligence)…
              </p>
            ) : null}

            {odintError ? <p className="text-sm text-destructive">{odintError}</p> : null}

            {!odintLoading && odintEmpty && !odintError && odintChoices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucun résultat ODINT pour cette recherche.
              </p>
            ) : null}

            {!odintLoading && odintChoices.length >= 2 ? (
              <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                <p className="text-sm text-foreground">
                  Plusieurs fiches avec un numéro de téléphone ont été trouvées. Sélectionnez
                  celle à utiliser pour le prospect.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {odintChoices.map((option, index) => {
                    const c = option.candidate || {};
                    const key = c.id || `odint-choice-${index}`;
                    const selected = odintSelectedId === key;
                    const phone =
                      c.phone || c.mobilePhone || 'Téléphone non renseigné';
                    const addressLine = [c.address, c.postalCode, c.city]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => selectOdintChoice({ ...option, candidate: { ...c, id: key } })}
                        className={`text-left rounded-lg border bg-card p-4 transition-smooth hover:bg-muted/30 ${
                          selected
                            ? 'border-primary ring-2 ring-primary/30'
                            : 'border-border'
                        }`}
                      >
                        <p className="font-semibold text-foreground text-sm mb-2">
                          {c.name || 'Sans nom'}
                        </p>
                        <p className="text-sm text-muted-foreground">{phone}</p>
                        {addressLine ? (
                          <p className="text-xs text-muted-foreground mt-1">{addressLine}</p>
                        ) : null}
                        {c.website ? (
                          <p className="text-xs text-primary mt-1 truncate">{c.website}</p>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {!odintLoading &&
            odintCandidate &&
            (odintChoices.length < 2 || odintSelectedId) ? (
              <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                <p className="text-sm text-foreground">
                  {odintChoices.length >= 2
                    ? 'Fiche sélectionnée. Souhaitez-vous créer un prospect avec ces informations ?'
                    : 'Un résultat a été trouvé. Souhaitez-vous créer une fiche prospect avec ces informations ?'}
                </p>
                {odintFields.length > 0 ? (
                  <div className="rounded-lg border border-border overflow-hidden bg-card">
                    <div className="divide-y divide-border">
                      {odintFields.map((field) => (
                        <div
                          key={field.key}
                          className="px-3 py-2 text-sm flex justify-between gap-4"
                        >
                          <span className="text-muted-foreground">{field.label}</span>
                          <span className="text-foreground font-medium text-right">
                            {field.suggested}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    loading={odintCreating}
                    disabled={odintCreating}
                    onClick={createProspectFromOdint}
                  >
                    Créer une fiche prospect
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={odintCreating}
                    onClick={clearOdintResult}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {searchResult ? (
          <section className="space-y-4">
            <p className="text-sm text-muted-foreground">
              (
              <span className="font-medium text-foreground">
                {totalCapped
                  ? '≥ 10 000'
                  : new Intl.NumberFormat('fr-FR').format(totalLegalUnits)}
              </span>{' '}
              établissements au total ({pageNum}/{totalPages} pages))
            </p>

            <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-foreground">
                  Rechercher un nom dans ce périmètre
                </p>
                <InfoTooltip text={TABLE_NAME_SEARCH_TOOLTIP} />
              </div>
              <p className="text-xs text-muted-foreground">
                Nom recherché :{' '}
                <span className="font-medium text-foreground">
                  {activeTableKeyword || 'aucun (liste complète CP + APE)'}
                </span>
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1 min-w-0">
                  <Input
                    label="Raison sociale (terme partiel)"
                    labelTooltip={TABLE_NAME_SEARCH_TOOLTIP}
                    value={tableNameQ}
                    onChange={(e) => setTableNameQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        runTableNameSearch();
                      }
                    }}
                  />
                </div>
                <Button
                  type="button"
                  iconName="Search"
                  disabled={searching || !tableNameQ.trim()}
                  loading={searching}
                  onClick={runTableNameSearch}
                >
                  Rechercher le nom
                </Button>
                {activeTableKeyword || tableNameQ.trim() ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={searching}
                    onClick={clearTableNameSearch}
                  >
                    Tout réafficher
                  </Button>
                ) : null}
              </div>
              {tableNameError ? (
                <p className="text-sm text-destructive">{tableNameError}</p>
              ) : null}
            </div>

            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="border-b border-border">
                    <th className="w-10 px-3 py-2">
                      <input
                        type="checkbox"
                        className="rounded border-input"
                        checked={
                          results.length > 0 &&
                          results.every((r) => selectedKeys.has(rowKey(r)))
                        }
                        onChange={(e) => (e.target.checked ? selectAllOnPage() : clearSelection())}
                        aria-label="Tout sélectionner"
                      />
                    </th>
                    <th className="text-left px-3 py-2 font-medium">Raison sociale</th>
                    <th className="text-left px-3 py-2 font-medium">Contact / nom</th>
                    <th className="text-left px-3 py-2 font-medium">SIRET</th>
                    <th className="text-left px-3 py-2 font-medium">APE</th>
                    <th className="text-left px-3 py-2 font-medium min-w-[180px]">Adresse</th>
                    <th className="text-left px-3 py-2 font-medium">Ville</th>
                  </tr>
                </thead>
                <tbody>
                  {results.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-3 py-8 text-center text-sm text-muted-foreground"
                      >
                        {activeTableKeyword
                          ? `Aucun établissement ne correspond à « ${activeTableKeyword} » dans ce code postal / APE.`
                          : 'Aucun établissement sur cette page.'}
                      </td>
                    </tr>
                  ) : null}
                  {results.map((r) => {
                    const key = rowKey(r);
                    return (
                      <tr key={key} className="border-b border-border/60 hover:bg-muted/20">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            className="rounded border-input"
                            checked={selectedKeys.has(key)}
                            onChange={() => toggleRow(key)}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium">{r.company || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.fullName || '—'}</td>
                        <td className="px-3 py-2 tabular-nums text-xs">{r.siret || '—'}</td>
                        <td className="px-3 py-2">{r.ape || '—'}</td>
                        <td
                          className="px-3 py-2 text-xs text-muted-foreground max-w-[240px] truncate"
                          title={r.address || ''}
                        >
                          {r.address || '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.postalCode} {r.city}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <div className="flex items-center justify-between gap-2 w-full">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || searching}
                  onClick={() => runSearch(page - 1)}
                >
                  Page précédente
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || searching}
                  onClick={() => runSearch(page + 1)}
                >
                  Page suivante
                </Button>
              </div>
            ) : null}

            <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Campagne (optionnel)
                </label>
                <select
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                  disabled={loadingCampaigns}
                  className="w-full max-w-md rounded-md border border-input bg-background px-3 py-2.5 text-sm"
                >
                  <option value="">
                    {loadingCampaigns ? 'Chargement…' : 'Aucune campagne'}
                  </option>
                  {campaignOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <TagMultiSelect
                label="Tags additionnels"
                value={tagsForAll}
                onChange={setTagsForAll}
                placeholder="Tags appliqués aux fiches importées…"
              />
            </div>

            {!importResult && !importing ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  iconName="Download"
                  disabled={selectedKeys.size === 0 || backgroundImportRunning}
                  onClick={handleImport}
                >
                  Importer la sélection
                  {selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ''}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  iconName="Download"
                  disabled={totalLegalUnits === 0 || backgroundImportRunning}
                  onClick={handleImportAll}
                >
                  Tout importer
                  {totalPages > 1
                    ? ` (${totalCapped ? '≥ ' : ''}${totalPages} pages API)`
                    : ''}
                </Button>
              </div>
            ) : null}
            {importError && !importResult ? (
              <p className="text-sm text-destructive">{importError}</p>
            ) : null}
            {importResult ? (
              <div className="rounded-xl border border-success/30 bg-success/5 p-4 text-sm">
                {importError ? (
                  <p className="mb-2 text-sm text-amber-800 dark:text-amber-300">{importError}</p>
                ) : null}
                <p className="font-medium text-foreground">
                  {importedTotal} fiche(s) importée(s) ({importResult.created} créée(s),{' '}
                  {importResult.updated} mise(s) à jour, {importResult.skipped} ignorée(s)).
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to="/prospect-contact-directory">
                    <Button size="sm">Voir l&apos;annuaire</Button>
                  </Link>
                  <Button size="sm" variant="outline" onClick={() => setImportResult(null)}>
                    Continuer la recherche
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}
      </main>
    </div>
  );
}
